import React, { useMemo, useState } from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import {
  AppleIcon,
  CheckIcon,
  GooglePlayIcon,
  MinusIcon,
  SortArrowsIcon,
  StarIcon,
  TrashIcon,
} from "./icons";
import {
  MyAppRow,
  MyAppsSortKey,
  MyAppsSortState,
  MyAppsStatus,
  MyAppsStore,
  MyAppsTableProps,
} from "./types";

const COLUMNS: Array<{
  key: MyAppsSortKey;
  label: string;
  className: string;
}> = [
  { key: "store", label: "Store", className: styles.colStore },
  { key: "country", label: "Country", className: styles.colCountry },
  { key: "rating", label: "Ratings", className: styles.colNum },
  { key: "reviews", label: "Reviews", className: styles.colNum },
  { key: "version", label: "Version", className: styles.colNum },
  { key: "lastUpdate", label: "Last update", className: styles.colDate },
  { key: "size", label: "Size (Mb)", className: styles.colNum },
];

const formatBigNumber = (n: number): string => {
  if (n < 1e3) return n.toFixed(1);
  if (n < 1e6) return `${+(n / 1e3).toFixed(1)}K`;
  if (n < 1e9) return `${+(n / 1e6).toFixed(1)}M`;
  return n.toFixed(1);
};

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${d.getFullYear()}`;
};

// "2.15.3" vs "2.9" — numeric per segment, not lexicographic.
const compareVersions = (a: string, b: string): number => {
  const pa = a.split(".").map((s) => parseInt(s, 10) || 0);
  const pb = b.split(".").map((s) => parseInt(s, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i += 1) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d) return d;
  }
  return 0;
};

const sortValue = (app: MyAppRow, key: MyAppsSortKey) => {
  switch (key) {
    case "name":
      return app.name ? app.name.toLowerCase() : null;
    case "store":
      return app.store;
    case "country":
      return app.country ? app.country.toLowerCase() : null;
    case "rating":
      return app.rating;
    case "reviews":
      return app.reviews;
    case "version":
      return app.version || null;
    case "lastUpdate":
      return app.updatedAt ? Date.parse(app.updatedAt) : null;
    case "size":
      return app.sizeMb;
    default:
      return null;
  }
};

// Stable sort; rows with no value always sink to the bottom, both directions.
const sortApps = (
  apps: MyAppRow[],
  sort: NonNullable<MyAppsSortState>,
): MyAppRow[] => {
  const dir = sort.direction === "asc" ? 1 : -1;
  return apps
    .map((app, index) => ({ app, index }))
    .sort((a, b) => {
      const va = sortValue(a.app, sort.key);
      const vb = sortValue(b.app, sort.key);
      if (va === null && vb === null) return a.index - b.index;
      if (va === null) return 1;
      if (vb === null) return -1;
      let d: number;
      if (sort.key === "version") {
        d = compareVersions(va as string, vb as string);
      } else if (typeof va === "string") {
        d = (va as string).localeCompare(vb as string);
      } else {
        d = (va as number) - (vb as number);
      }
      return d ? d * dir : a.index - b.index;
    })
    .map(({ app }) => app);
};

const matchesFilters = (
  app: MyAppRow,
  query: string,
  storeFilter: MyAppsStore | "all",
  statusFilter: MyAppsStatus | "all",
): boolean => {
  if (storeFilter !== "all" && app.store !== storeFilter) return false;
  if (statusFilter !== "all" && app.status !== statusFilter) return false;
  const q = query.trim().toLowerCase();
  if (q && !app.name.toLowerCase().includes(q)) return false;
  return true;
};

const Checkbox: React.FC<{
  checked: boolean;
  indeterminate?: boolean;
  ariaLabel: string;
  onToggle: () => void;
}> = ({ checked, indeterminate = false, ariaLabel, onToggle }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={indeterminate ? "mixed" : checked}
    aria-label={ariaLabel}
    className={cs(styles.checkbox, {
      [styles.checkboxOn]: checked || indeterminate,
    })}
    onClick={(e) => {
      e.stopPropagation();
      onToggle();
    }}
  >
    {indeterminate ? (
      <MinusIcon className={styles.checkboxIcon} />
    ) : checked ? (
      <CheckIcon className={styles.checkboxIcon} />
    ) : null}
  </button>
);

const AppIcon: React.FC<{ iconUrl?: string; store: MyAppsStore }> = ({
  iconUrl,
  store,
}) => {
  const [failed, setFailed] = useState(false);
  return iconUrl && !failed ? (
    <span className={styles.appIcon}>
      <img src={iconUrl} alt="" onError={() => setFailed(true)} />
    </span>
  ) : (
    <span className={cs(styles.appIcon, styles.appIconFallback)}>
      {store === "apple" ? (
        <AppleIcon className={styles.storeGlyph} />
      ) : (
        <GooglePlayIcon className={styles.storeGlyph} />
      )}
    </span>
  );
};

/**
 * My Apps table (Figma 563:17269). Pure presentational, token-based.
 * Sorting and selection are controlled-with-uncontrolled-fallback: pass
 * `sort`/`onSortChange` and `selectedIds`/`onSelectionChange` from a container
 * (prod), or omit them and the table manages its own state (sandbox).
 */
export const MyAppsTable: React.FC<MyAppsTableProps> = ({
  apps,
  sort,
  defaultSort = null,
  onSortChange,
  clientSort = true,
  selectedIds,
  defaultSelectedIds = [],
  onSelectionChange,
  query = "",
  storeFilter = "all",
  statusFilter = "all",
  onRowClick,
  onDelete,
  emptyText = "No apps found",
  className,
}) => {
  const [innerSort, setInnerSort] = useState<MyAppsSortState>(defaultSort);
  const sortState = sort !== undefined ? sort : innerSort;

  const [innerSelected, setInnerSelected] =
    useState<string[]>(defaultSelectedIds);
  const selected = selectedIds !== undefined ? selectedIds : innerSelected;

  const changeSort = (key: MyAppsSortKey) => {
    // Same column toggles asc/desc; a new column always starts asc.
    const next: MyAppsSortState =
      sortState?.key === key
        ? { key, direction: sortState.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" };
    if (sort === undefined) setInnerSort(next);
    onSortChange?.(next);
  };

  const changeSelection = (ids: string[]) => {
    if (selectedIds === undefined) setInnerSelected(ids);
    onSelectionChange?.(ids);
  };

  const visibleApps = useMemo(() => {
    const filtered = apps.filter((app) =>
      matchesFilters(app, query, storeFilter, statusFilter),
    );
    return clientSort && sortState ? sortApps(filtered, sortState) : filtered;
  }, [apps, query, storeFilter, statusFilter, clientSort, sortState]);

  // "Select all" acts on visible rows only; rows hidden by a filter keep
  // their selection so clearing the filter restores it.
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const visibleSelectedCount = visibleApps.filter((app) =>
    selectedSet.has(app.id),
  ).length;
  const allChecked =
    visibleApps.length > 0 && visibleSelectedCount === visibleApps.length;
  const someChecked = visibleSelectedCount > 0 && !allChecked;

  const toggleAll = () => {
    const visibleIds = new Set(visibleApps.map((app) => app.id));
    if (allChecked) {
      changeSelection(selected.filter((id) => !visibleIds.has(id)));
    } else {
      const merged = new Set(selected);
      visibleIds.forEach((id) => merged.add(id));
      changeSelection(Array.from(merged));
    }
  };

  const toggleRow = (id: string) => {
    changeSelection(
      selectedSet.has(id)
        ? selected.filter((x) => x !== id)
        : [...selected, id],
    );
  };

  const renderSortArrows = (key: MyAppsSortKey) => {
    const active = sortState?.key === key;
    return (
      <SortArrowsIcon
        className={styles.sortArrows}
        upClassName={
          active && sortState?.direction === "asc" ? styles.sortActive : undefined
        }
        downClassName={
          active && sortState?.direction === "desc" ? styles.sortActive : undefined
        }
      />
    );
  };

  return (
    <div className={cs(styles.tableContainer, className)}>
      <div className={styles.table}>
        <div className={styles.headerRow}>
          <div className={cs(styles.headerCell, styles.colApp)}>
            <Checkbox
              checked={allChecked}
              indeterminate={someChecked}
              ariaLabel="Select all apps"
              onToggle={toggleAll}
            />
            <button
              type="button"
              className={styles.headerLabel}
              onClick={() => changeSort("name")}
            >
              App
              {renderSortArrows("name")}
            </button>
          </div>
          {COLUMNS.map((col) => (
            <div key={col.key} className={cs(styles.headerCell, col.className)}>
              <button
                type="button"
                className={styles.headerLabel}
                onClick={() => changeSort(col.key)}
              >
                {col.label}
                {renderSortArrows(col.key)}
              </button>
            </div>
          ))}
          <div className={cs(styles.headerCell, styles.colDelete)} />
        </div>

        {visibleApps.length ? (
          visibleApps.map((app) => (
            <div
              key={app.id}
              className={cs(styles.row, {
                [styles.rowClickable]: Boolean(onRowClick),
              })}
              onClick={onRowClick ? () => onRowClick(app) : undefined}
            >
              <div className={cs(styles.cell, styles.colApp)}>
                <Checkbox
                  checked={selectedSet.has(app.id)}
                  ariaLabel={`Select ${app.name}`}
                  onToggle={() => toggleRow(app.id)}
                />
                <div className={styles.appInfo}>
                  <AppIcon iconUrl={app.iconUrl} store={app.store} />
                  <div className={styles.appMeta}>
                    <span className={styles.appName} title={app.name}>
                      {app.name}
                    </span>
                    <span
                      className={cs(
                        styles.statusBadge,
                        app.status === "available"
                          ? styles.statusAvailable
                          : styles.statusUnavailable,
                      )}
                    >
                      {app.status === "available" ? "Available" : "Unavailable"}
                    </span>
                  </div>
                </div>
              </div>

              <div className={cs(styles.cell, styles.colStore)}>
                <span className={styles.storeBadge}>
                  {app.store === "apple" ? (
                    <AppleIcon className={styles.storeGlyph} />
                  ) : (
                    <GooglePlayIcon className={styles.storeGlyph} />
                  )}
                </span>
              </div>

              <div className={cs(styles.cell, styles.colCountry, styles.country)}>
                {app.country || "-"}
              </div>

              <div className={cs(styles.cell, styles.colNum)}>
                <StarIcon className={styles.star} />
                {app.rating != null ? app.rating.toFixed(1) : "0.0"}
              </div>

              <div className={cs(styles.cell, styles.colNum)}>
                <StarIcon className={styles.star} />
                {app.reviews != null ? formatBigNumber(app.reviews) : "0.0"}
              </div>

              <div className={cs(styles.cell, styles.colNum)}>
                {app.version || "-"}
              </div>

              <div className={cs(styles.cell, styles.colDate, styles.muted)}>
                {app.updatedAt ? formatDate(app.updatedAt) : "-"}
              </div>

              <div className={cs(styles.cell, styles.colNum, styles.muted)}>
                {app.sizeMb != null && app.sizeMb >= 0.1
                  ? app.sizeMb.toFixed(1)
                  : "-"}
              </div>

              <div className={cs(styles.cell, styles.colDelete)}>
                {onDelete ? (
                  <button
                    type="button"
                    className={styles.deleteBtn}
                    aria-label={`Delete ${app.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(app);
                    }}
                  >
                    <TrashIcon className={styles.trashIcon} />
                  </button>
                ) : null}
              </div>
            </div>
          ))
        ) : (
          <div className={styles.empty}>
            <p className={styles.emptyText}>{emptyText}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyAppsTable;
